// Share of total on a ranked list — ported from RetailMaxx (src/shared/share.ts, owner 27 Oct 2026, 23:37): "how much 50% of
// total how many parties contribute. and i can change the percentage numbers.. Like top 10.. and how much are they
// contributing.. Give me such in all app".
//
// Two questions, on any list ranked by an amount that adds up (sales, units, stock, lost sales):
//   what do the top N bring?            "The top 10 dealers bring ₹4.21 Cr: 62% of ₹6.80 Cr."
//   how few make up X% of the total?    "7 of 312 dealers (2.2%) make up 50%: they bring ₹3.61 Cr, 53% of ₹6.80 Cr."
// Shares are of the total the list shows: the net of every row with a figure, so "50% of total" is half the figure on the
// screen and the top 10's share is the one the trade screens already give. A party that returned more than it bought is
// never ranked, but its returns come off the total, and that is said. A row with no figure is left out and said so, never
// counted as zero. Where returns exceed sales there is no share to give.

/** The amounts above zero, largest first; the net total they are shares of; and what is not ranked. */
export interface ShareCurve { pos: number[]; base: number; neg: { n: number; sum: number }; zero: number; missing: number }
/** A cut of the ranked list: how many, what they bring, and their share of the base (null with nothing above zero). */
export interface Cut { n: number; sum: number; share: number | null }

export function curveOf(values: readonly (number | null | undefined)[]): ShareCurve {
  const pos: number[] = []; const neg = { n: 0, sum: 0 }; let zero = 0, missing = 0;
  for (const v of values) {
    if (v === null || v === undefined || !Number.isFinite(v)) missing++;
    else if (v > 0) pos.push(v);
    else if (v < 0) { neg.n++; neg.sum += v; }
    else zero++;
  }
  pos.sort((a, b) => b - a);
  return { pos, base: pos.reduce((t, v) => t + v, 0) + neg.sum, neg, zero, missing };
}

const cutAt = (c: ShareCurve, n: number): Cut => {
  if (!c.pos.length || !(c.base > 0)) return { n: 0, sum: 0, share: null };
  let sum = 0; for (let i = 0; i < n; i++) sum += c.pos[i]!;
  return { n, sum, share: n === c.pos.length && !c.neg.n ? 1 : sum / c.base };
};

/** What the top N bring. N is read as a whole number, at least 1, at most all of them. */
export function cutTop(c: ShareCurve, n: number): Cut {
  const k = Math.min(c.pos.length, Math.max(1, Number.isFinite(n) ? Math.floor(n) : 1));
  return cutAt(c, k);
}

/** The fewest whose amounts reach pct% of the total (pct read as 1 to 100). */
export function cutReach(c: ShareCurve, pct: number): Cut {
  if (!c.pos.length || !(c.base > 0)) return { n: 0, sum: 0, share: null };
  const p = Math.min(100, Math.max(1, Number.isFinite(pct) ? pct : 1));
  const need = c.base * p / 100 * (1 - 1e-9);   // 0.1 + 0.2 reaches 0.3; a sum is never short by rounding
  let sum = 0, k = 0;
  while (k < c.pos.length && sum < need) sum += c.pos[k++]!;
  return cutAt(c, k);
}

/** The rows' keys in rank order (largest first, ties by key), those above zero only: the rows a cut of n marks are the first n. */
export function rankKeys<R>(rows: readonly R[], value: (r: R) => number | null | undefined, key: (r: R) => string): string[] {
  return rows.map(r => ({ k: key(r), v: value(r) }))
    .filter((x): x is { k: string; v: number } => typeof x.v === 'number' && Number.isFinite(x.v) && x.v > 0)
    .sort((a, b) => b.v - a.v || (a.k < b.k ? -1 : a.k > b.k ? 1 : 0)).map(x => x.k);
}

/** How a list names its rows and its amount. `measure` is what the amount is ("sales", "stock"); `neg` says what a row
 *  below zero means (a dealer "returned more than it bought"; by default "had more returns than sales"). */
export interface ShareWords { one: string; many: string; fmt(n: number): string; measure?: string; neg?: { one: string; many: string }; /** what the rows do with the amount: sales are brought (the default), stock is held */ verb?: { one: string; many: string } }

const share = (x: number) => { if (x > 0 && x < 0.001) return 'under 0.1%'; const t = (x * 100).toFixed(x < 0.1 && x > 0 ? 1 : 0); return t.replace(/\.0$/, '') + '%'; };
const count = (n: number, w: ShareWords) => `${n.toLocaleString('en-IN')} ${n === 1 ? w.one : w.many}`;

/** Why there is no share: nothing to rank, or returns that exceed sales. */
const noShare = (c: ShareCurve, w: ShareWords) => c.pos.length && c.neg.n ? `Returns exceed ${w.measure ?? 'sales'} here, so there is no share of a total to give.` : `No ${w.many} with ${w.measure ?? 'sales'} to rank.`;
/** Rows that are not ranked (below zero, zero) make "dealers" into "dealers with sales". */
const ranked = (c: ShareCurve, w: ShareWords) => `${count(c.pos.length, w)}${c.neg.n || c.zero ? ` with ${w.measure ?? 'sales'}` : ''}`;

export function topText(c: ShareCurve, cut: Cut, w: ShareWords): string {
  if (cut.share === null) return noShare(c, w);
  const all = cut.n === c.pos.length && c.pos.length > 1;
  const who = all ? (c.neg.n || c.zero ? `The ${ranked(c, w)}` : `All ${count(cut.n, w)}`) : cut.n === 1 ? `The top ${w.one}` : `The top ${count(cut.n, w)}`;
  const v = w.verb ?? { one: 'brings', many: 'bring' };
  return `${who} ${cut.n === 1 ? v.one : v.many} ${w.fmt(cut.sum)}: ${share(cut.share)} of ${w.fmt(c.base)}.`;
}

export function reachText(c: ShareCurve, cut: Cut, pct: number, w: ShareWords): string {
  if (cut.share === null) return noShare(c, w);
  const p = Math.min(100, Math.max(1, Number.isFinite(pct) ? pct : 1));
  const one = cut.n === 1;
  const v = w.verb ?? { one: 'brings', many: 'bring' };
  return `${cut.n.toLocaleString('en-IN')} of ${ranked(c, w)} (${share(cut.n / c.pos.length)}) ${one ? 'makes' : 'make'} up ${String(p).replace(/\.0+$/, '')}%: ${one ? `it ${v.one}` : `they ${v.many}`} ${w.fmt(cut.sum)}, ${share(cut.share)} of ${w.fmt(c.base)}.`;
}

/** What is not ranked, and how it bears on the total; null when nothing is. */
export function leftOutText(c: ShareCurve, w: ShareWords): string | null {
  const parts: string[] = [];
  const neg = w.neg ?? { one: 'had more returns than sales', many: 'had more returns than sales' };
  if (c.neg.n) parts.push(`${count(c.neg.n, w)} ${c.neg.n === 1 ? neg.one : neg.many} (${w.fmt(c.neg.sum)}): the total, ${w.fmt(c.base)}, is after ${c.neg.n === 1 ? 'it' : 'them'}.`);
  if (c.missing) parts.push(`${c.missing.toLocaleString('en-IN')} with no figure ${c.missing === 1 ? 'is' : 'are'} left out.`);
  return parts.length ? parts.join(' ') : null;
}
